/**
 * Velociraptor Offline Collector Configuration Modal
 *
 * Standalone modal for creating and editing Velociraptor offline collector
 * templates. Callable from Collection Wizard, Org Admin, or Case Settings.
 */

import { type ReactElement } from 'react'
import type { ArtefactTemplate, ArtefactTemplateScope } from '../../types/artefact-templates'
import type { ArtefactConfig } from '../../types/velociraptor'
import { useVelociraptorArtefacts } from '../../hooks/use-velociraptor'
import type { TemplateUserRole } from '../../utils/template-permissions'
import {
  ConfigModalHeader,
  ConfigTabsBar,
  ConfigBodySwitch,
  ConfigSaveBar,
} from './velociraptor-config-modal.parts'
import { useVelociraptorConfigOrchestration } from './velociraptor-config-modal.state'

interface PlatformMutations {
  create: (input: {
    name: string
    description?: string
    artefacts: ArtefactConfig[]
    supportedPlatforms?: string[]
  }) => Promise<ArtefactTemplate | null>
  update: (
    templateId: string,
    input: {
      name?: string
      description?: string
      artefacts?: ArtefactConfig[]
      supportedPlatforms?: string[]
    }
  ) => Promise<ArtefactTemplate | null>
  creating: boolean
  updating: boolean
  error: string | null
}

interface VelociraptorConfigModalProps {
  isOpen: boolean
  onClose: () => void
  onTemplateSaved: (template: ArtefactTemplate) => void
  editTemplate?: ArtefactTemplate | null | undefined
  context: {
    scope: ArtefactTemplateScope
    caseId?: string | undefined
    userRole: TemplateUserRole
  }
  platformMutations?: PlatformMutations | undefined
}

export function VelociraptorConfigModal({
  isOpen,
  onClose,
  onTemplateSaved,
  editTemplate,
  context,
  platformMutations,
}: VelociraptorConfigModalProps): ReactElement | null {
  const artefactsQuery = useVelociraptorArtefacts()
  const orchestration = useVelociraptorConfigOrchestration({
    isOpen,
    editTemplate,
    context,
    platformMutations,
    onTemplateSaved,
    onClose,
  })

  if (!isOpen) return null

  const { form, saveAction, isEditMode, saving, canSave, mutationsError, handleSave } =
    orchestration

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl dark:bg-slate-900">
        <ConfigModalHeader isEditMode={isEditMode} onClose={onClose} />
        <ConfigTabsBar
          activeSection={form.activeSection}
          selectedCount={form.selectedArtefacts.length}
          onSelect={form.setActiveSection}
        />
        <ConfigBodySwitch
          activeSection={form.activeSection}
          metadata={{
            name: form.name,
            description: form.description,
            platforms: form.platforms,
            onNameChange: form.setName,
            onDescriptionChange: form.setDescription,
            onTogglePlatform: form.togglePlatform,
          }}
          artefacts={{
            artefacts: artefactsQuery.artefacts,
            artefactsByCategory: artefactsQuery.artefactsByCategory,
            selectedArtefacts: form.selectedArtefacts,
            parameterOverrides: form.parameterOverrides,
            loading: artefactsQuery.loading,
            error: artefactsQuery.error,
            onSelect: form.handleSelectArtefact,
            onDeselect: form.handleDeselectArtefact,
            onParameterChange: form.handleParameterChange,
            onRefresh: () => {
              void artefactsQuery.refetch()
            },
          }}
          settings={{ value: form.collectorSettings, onChange: form.setCollectorSettings }}
        />
        <ConfigSaveBar
          errorMessage={form.saveError ?? mutationsError}
          selectedCount={form.selectedArtefacts.length}
          saving={saving}
          saveAction={saveAction}
          isEditMode={isEditMode}
          canSave={canSave}
          onSave={handleSave}
          onClose={onClose}
        />
      </div>
    </div>
  )
}
